import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, saveSessionToken, savePwaNonce } from '../utils/authToken';

const API = `${window.location.origin}/api`;
const LOGO_URL = "/blackified-logo.png";

export default function AuthPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register', 'forgot-password', 'verification-sent'
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);
        
        if (response.ok) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Auth check error:', error);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (authMode !== 'forgot-password') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }
    
    if (authMode === 'register') {
      if (!formData.name) {
        newErrors.name = 'Name is required';
      } else if (formData.name.length < 2) {
        newErrors.name = 'Name must be at least 2 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      if (authMode === 'login') {
        const response = await fetch(`${API}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });
        
        if (response.ok) {
          toast.success('Welcome back!');
          navigate('/dashboard', { replace: true });
        } else {
          const error = await response.json();
          if (response.status === 403) {
            // Email not verified
            setRegisteredEmail(formData.email);
            setAuthMode('verification-sent');
            toast.info(error.detail);
          } else {
            toast.error(error.detail || 'Login failed');
          }
        }
      } else if (authMode === 'register') {
        const response = await fetch(`${API}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            name: formData.name
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setRegisteredEmail(formData.email);
          setAuthMode('verification-sent');
          
          if (data.verification_email_sent) {
            toast.success('Account created! Check your email to verify.');
          } else {
            toast.success('Account created! Please verify your email to sign in.');
          }
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Registration failed');
        }
      } else if (authMode === 'forgot-password') {
        const response = await fetch(`${API}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email })
        });
        
        if (response.ok) {
          toast.success('If an account exists, a password reset link has been sent.');
          setAuthMode('login');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Something went wrong');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail })
      });
      
      if (response.ok) {
        toast.success('Verification email sent!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Could not send verification email');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const isIOSPWA = window.navigator.standalone === true;
    let redirectUrl = window.location.origin + '/auth';

    if (isIOSPWA) {
      // Generate a nonce and store it in the PWA's own localStorage.
      // After auth completes in Safari, the callback will save the session
      // server-side keyed by this nonce. When the user returns to the PWA,
      // App.js will claim the session using the nonce.
      const nonce = crypto.randomUUID ? crypto.randomUUID() : 'n_' + Date.now() + '_' + Math.random().toString(36).slice(2);
      savePwaNonce(nonce);
      redirectUrl = window.location.origin + '/auth?pwa_nonce=' + encodeURIComponent(nonce);
    }

    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', confirmPassword: '' });
    setErrors({});
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm">Back</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Blackified" 
              className="h-16 md:h-20 mx-auto mb-4 object-contain"
            />
          </div>

          {/* Auth Card */}
          <div className="glass-card rounded-xl p-8">
            <AnimatePresence mode="wait">
              {authMode === 'verification-sent' ? (
                <motion.div
                  key="verification"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                    <Mail className="w-8 h-8 text-green-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
                  <p className="text-zinc-400 mb-6">
                    We've sent a verification link to <span className="text-white font-medium">{registeredEmail}</span>. 
                    Please check your inbox and click the link to activate your account.
                  </p>
                  <p className="text-zinc-500 text-sm mb-6">
                    Can't find the email? Check your spam folder or request a new one.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={handleResendVerification}
                      disabled={submitting}
                      className="w-full py-3 px-4 rounded-full border border-zinc-700 text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
                      data-testid="resend-verification-btn"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Resend Verification Email'}
                    </button>
                    <button
                      onClick={() => {
                        resetForm();
                        setAuthMode('login');
                      }}
                      className="w-full py-3 px-4 rounded-full text-zinc-400 hover:text-white transition-colors"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={authMode}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <h1 className="text-2xl font-bold text-white text-center mb-6">
                    {authMode === 'login' && 'Welcome Back'}
                    {authMode === 'register' && 'Create Account'}
                    {authMode === 'forgot-password' && 'Reset Password'}
                  </h1>

                  {/* Google Sign In Button */}
                  {authMode !== 'forgot-password' && (
                    <>
                      <button
                        onClick={handleGoogleLogin}
                        className="w-full flex items-center justify-center gap-3 bg-white text-black font-medium py-3 px-6 rounded-full hover:bg-zinc-200 transition-colors"
                        data-testid="google-signin-button"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continue with Google
                      </button>

                      {/* Divider */}
                      <div className="flex items-center gap-4 my-6">
                        <div className="flex-1 h-px bg-zinc-800" />
                        <span className="text-zinc-600 text-xs font-mono">OR</span>
                        <div className="flex-1 h-px bg-zinc-800" />
                      </div>
                    </>
                  )}

                  {/* Email/Password Form */}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {authMode === 'register' && (
                      <div>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                          <input
                            type="text"
                            placeholder="Full Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className={`w-full pl-12 pr-4 py-3 rounded-lg bg-zinc-900 border ${errors.name ? 'border-red-500' : 'border-zinc-800'} text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700`}
                            data-testid="name-input"
                          />
                        </div>
                        {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                      </div>
                    )}

                    <div>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                        <input
                          type="email"
                          placeholder="Email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className={`w-full pl-12 pr-4 py-3 rounded-lg bg-zinc-900 border ${errors.email ? 'border-red-500' : 'border-zinc-800'} text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700`}
                          data-testid="email-input"
                        />
                      </div>
                      {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                    </div>

                    {authMode !== 'forgot-password' && (
                      <div>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className={`w-full pl-12 pr-12 py-3 rounded-lg bg-zinc-900 border ${errors.password ? 'border-red-500' : 'border-zinc-800'} text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700`}
                            data-testid="password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
                      </div>
                    )}

                    {authMode === 'register' && (
                      <div>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Confirm Password"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            className={`w-full pl-12 pr-4 py-3 rounded-lg bg-zinc-900 border ${errors.confirmPassword ? 'border-red-500' : 'border-zinc-800'} text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700`}
                            data-testid="confirm-password-input"
                          />
                        </div>
                        {errors.confirmPassword && <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>}
                      </div>
                    )}

                    {authMode === 'login' && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            resetForm();
                            setAuthMode('forgot-password');
                          }}
                          className="text-sm text-zinc-400 hover:text-white transition-colors"
                          data-testid="forgot-password-link"
                        >
                          Forgot password?
                        </button>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 px-4 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="submit-auth-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          {authMode === 'login' && 'Sign In'}
                          {authMode === 'register' && 'Create Account'}
                          {authMode === 'forgot-password' && 'Send Reset Link'}
                        </>
                      )}
                    </button>
                  </form>

                  {/* Toggle Auth Mode */}
                  <div className="mt-6 text-center">
                    {authMode === 'login' && (
                      <p className="text-zinc-500 text-sm">
                        Don't have an account?{' '}
                        <button
                          onClick={() => {
                            resetForm();
                            setAuthMode('register');
                          }}
                          className="text-white hover:underline"
                          data-testid="switch-to-register"
                        >
                          Sign up
                        </button>
                      </p>
                    )}
                    {authMode === 'register' && (
                      <p className="text-zinc-500 text-sm">
                        Already have an account?{' '}
                        <button
                          onClick={() => {
                            resetForm();
                            setAuthMode('login');
                          }}
                          className="text-white hover:underline"
                          data-testid="switch-to-login"
                        >
                          Sign in
                        </button>
                      </p>
                    )}
                    {authMode === 'forgot-password' && (
                      <p className="text-zinc-500 text-sm">
                        Remember your password?{' '}
                        <button
                          onClick={() => {
                            resetForm();
                            setAuthMode('login');
                          }}
                          className="text-white hover:underline"
                        >
                          Back to sign in
                        </button>
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Terms */}
          {authMode !== 'verification-sent' && (
            <p className="mt-6 text-center text-zinc-600 text-xs">
              By continuing, you agree to our Terms of Service and Privacy Policy.
            </p>
          )}
        </motion.div>
      </main>
    </div>
  );
}
